import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { 
  Lightning, 
  House, 
  Car, 
  ShieldWarning, 
  Fire, 
  Thermometer, 
  Wind, 
  Gear, 
  WifiHigh,
  Wrench
} from '@phosphor-icons/react';

const services = [
  {
    id: 'electricite',
    title: 'Électricité Générale',
    description: 'Installation, rénovation, mise aux normes. Tableaux électriques, prises, interrupteurs, éclairage.',
    icon: Lightning,
    image: '/images/electricite-generale.jpg',
    featured: true,
  },
  {
    id: 'domotique',
    title: 'Domotique',
    description: 'Maison connectée avec les solutions Legrand Céliane et Dooxie with Netatmo. Thermostats et visiophones connectés.',
    icon: House,
    image: '/images/domotique.jpg',
    featured: true,
  },
  {
    id: 'borne-ve',
    title: 'Borne de Recharge VE',
    description: 'Installation de bornes IRVE pour véhicules électriques. Prise Green\'Up et solutions adaptées.',
    icon: Car,
    image: '/images/borne-ve.jpg',
    featured: true,
  },
  {
    id: 'alarmes',
    title: 'Alarmes et Caméras',
    description: 'Systèmes filaires et radio pour sécuriser votre domicile ou vos locaux professionnels.',
    icon: ShieldWarning,
  },
  {
    id: 'incendie',
    title: 'Alarmes Incendie',
    description: 'Détecteurs de fumée et systèmes de sécurité incendie conformes aux normes.',
    icon: Fire,
  },
  {
    id: 'chauffage',
    title: 'Chauffage Électrique',
    description: 'Installation et remplacement de radiateurs électriques performants.',
    icon: Thermometer,
  },
  {
    id: 'climatisation',
    title: 'Climatisation',
    description: 'Installation et entretien de systèmes de climatisation splits et gainables.',
    icon: Wind,
  },
  {
    id: 'automatismes',
    title: 'Automatismes',
    description: 'Installation, dépannage et remplacement de motorisation de portails, portes de garage et volets roulants.',
    icon: Gear,
  },
  {
    id: 'reseau',
    title: 'Réseaux & VDI',
    description: 'Installation de réseaux informatiques et Wi-Fi performants.',
    icon: WifiHigh,
  },
  {
    id: 'depannage',
    title: 'Dépannage',
    description: 'Intervention rapide pour tous vos problèmes électriques urgents.',
    icon: Wrench,
  },
  {
    id: 'photovoltaique',
    title: 'Gestion Photovoltaïque',
    description: 'Installation de système de gestion photovoltaïque (routeur, batterie, solution connectée) pour optimiser votre production solaire.',
    icon: Lightning,
  },
];

const FeaturedServiceCard = ({ service, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group relative rounded-2xl overflow-hidden bg-[#0A0A0A] h-[300px] sm:h-[350px] md:h-[450px]"
      data-testid={`service-card-${service.id}`}
    >
      {/* Image */}
      <div className="absolute inset-0">
        <img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
      </div>

      {/* Content - Fixed positioning for alignment */}
      <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-8">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#D32F2F] flex items-center justify-center mb-3 sm:mb-4">
          <service.icon weight="bold" className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </div>
        <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2 sm:mb-3">
          {service.title}
        </h3>
        <p className="text-white/70 text-xs sm:text-sm md:text-base leading-relaxed">
          {service.description}
        </p>
      </div>
    </motion.div>
  );
};

const ServiceItem = ({ service, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-30px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="group bg-white rounded-2xl p-6 border border-black/5 hover:border-[#D32F2F]/20 hover:shadow-lg transition-all duration-300"
      data-testid={`service-item-${service.id}`}
    >
      <div className="w-14 h-14 rounded-xl bg-[#0A0A0A] flex items-center justify-center mb-4 group-hover:bg-[#D32F2F] transition-colors duration-300">
        <service.icon weight="bold" className="w-6 h-6 text-white" />
      </div>
      <h3 className="font-bold text-lg text-[#0A0A0A] mb-2">
        {service.title}
      </h3>
      <p className="text-sm text-[#4A4A4A] leading-relaxed">
        {service.description}
      </p>
    </motion.div>
  );
};

export const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const featuredServices = services.filter(s => s.featured);
  const otherServices = services.filter(s => !s.featured);

  return (
    <section 
      id="services" 
      className="py-16 sm:py-24 md:py-32 bg-[#FAFAFA]"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div ref={ref} className="max-w-3xl mb-10 sm:mb-16">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="inline-block text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#D32F2F] mb-4"
          >
            Nos services
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A]"
            data-testid="services-title"
          >
            Une expertise complète
            <span className="text-[#D32F2F]"> pour tous vos besoins</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-sm sm:text-base md:text-lg text-gray-600 mt-4 leading-relaxed"
          >
            De l'électricité générale aux solutions connectées, nous intervenons sur tous types de projets.
          </motion.p>
        </div>

        {/* Featured services - Large cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12">
          {featuredServices.map((service, index) => (
            <FeaturedServiceCard key={service.id} service={service} index={index} />
          ))}
        </div>

        {/* Other services - Grid cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <h3 className="text-2xl font-bold text-[#0A0A0A] mb-8 text-center">
            Tous nos services
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {otherServices.map((service, index) => (
              <ServiceItem key={service.id} service={service} index={index} />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
